import type {
  TestId,
  ValidateLowercase,
  ValidateLowercaseArray,
  ValidateLowercaseObject,
} from './types';

/**
 * Generates a `data-testid` attribute value for a component.
 *
 * @param {string} page - The name of the page where the component is used.
 * @param {string} action - The action the component performs (e.g., filter, login).
 * @param {string} htmlElement - The HTML element type (e.g., button, form, input).
 * @param {string} params - An optional string to be concatenated to the test id.
 *
 * @returns The generated `data-testid` value.
 *
 * @example
 * // Example usage:
 * const testId = generateTestId('login', 'login', 'button');
 * // testId: 'login-page-login-button'
 *
 * @example
 * // Example usage with params:
 * const testId = generateTestId('login', 'login', 'button', 'with', 'params');
 * // testId: 'login-page-login-button-with-params'
 */
export function generateTestId<
  TPage extends string,
  TAction extends string,
  TElement extends string,
  TParams extends string[],
>(
  page: ValidateLowercase<TPage>,
  action: ValidateLowercase<TAction>,
  htmlElement: ValidateLowercase<TElement>,
  ...params: ValidateLowercaseArray<TParams>
) {
  const idParts = [`${page}-page`, action, htmlElement, ...params];
  return idParts.join('-') as TestId<TPage, [TAction, TElement, ...TParams]>;
}

/**
 * Generates element name with data-testid object.
 *
 * @param {string} page - The name of the page where the element is used.
 * @param {string} elements - The object that contains key as element name and value as an array of string (to be used for data-testid generation).
 *
 * @returns The object that contains key as element name and value as data-testid.
 *
 */
export function generateTestIdObject<
  TPage extends string,
  TElements extends Record<string, [Lowercase<string>, Lowercase<string>, ...Lowercase<string>[]]>,
  TKey extends keyof TElements,
>(page: ValidateLowercase<TPage>, elements: ValidateLowercaseObject<TElements>) {
  return Object.fromEntries(
    Object.entries(elements).map(
      ([key, [action, htmlElement, ...params]]: [string, Lowercase<string>[]]) => {
        return [key, generateTestId(page, action, htmlElement, ...params)];
      },
    ),
  ) as { [K in TKey]: TestId<TPage, TElements[K]> };
}
